import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';

const TOOLTIP_WIDTH = 432;
const VIEWPORT_PADDING = 12;
const TOOLTIP_GAP = 8;

export default function InfoTooltip({ text, className = '' }) {
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);

  const computePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === 'undefined') return null;

    const rect = trigger.getBoundingClientRect();
    const halfWidth = TOOLTIP_WIDTH / 2;
    const minLeft = VIEWPORT_PADDING + halfWidth;
    const maxLeft = window.innerWidth - VIEWPORT_PADDING - halfWidth;
    const idealLeft = rect.left + rect.width / 2;
    const left = Math.min(Math.max(idealLeft, minLeft), maxLeft);

    let top = rect.bottom + TOOLTIP_GAP;
    const tooltipHeight = tooltipRef.current?.offsetHeight || 0;
    if (tooltipHeight && top + tooltipHeight > window.innerHeight - VIEWPORT_PADDING) {
      top = Math.max(VIEWPORT_PADDING, rect.top - tooltipHeight - TOOLTIP_GAP);
    }

    return { left, top };
  }, []);

  const updatePosition = useCallback(() => {
    const nextPosition = computePosition();
    if (nextPosition) setPosition(nextPosition);
  }, [computePosition]);

  const showTooltip = () => {
    setPosition(computePosition());
    setOpen(true);
  };

  const hideTooltip = () => {
    setOpen(false);
  };

  useLayoutEffect(() => {
    if (!open) return undefined;

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  const tooltip =
    open && position && typeof document !== 'undefined'
      ? createPortal(
          <span
            ref={tooltipRef}
            className="pointer-events-none fixed z-[2000] -translate-x-1/2 rounded-md border border-border bg-popover px-3 py-2 text-xs leading-relaxed text-popover-foreground shadow-elevation-2"
            style={{
              left: position.left,
              top: position.top,
              width: TOOLTIP_WIDTH,
              maxWidth: `calc(100vw - ${VIEWPORT_PADDING * 2}px)`,
            }}
          >
            {text}
          </span>,
          document.body,
        )
      : null;

  return (
    <span
      className={`inline-flex ${className}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      <button
        ref={triggerRef}
        type="button"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label="Information"
      >
        <Icon name="Info" size={14} />
      </button>
      {tooltip}
    </span>
  );
}
